import { Fragment, useEffect, useMemo, useState, type ReactElement } from 'react';
import { WELLS_API_URL } from '../../constants';
import type { RegionFieldDto, RegionMasterDto, WellMaster } from '../../domain/types';
import { findRegionField, regionDisplayName, toWellRegionFieldInfo } from '../../domain/wellRegions';
import { EntityActivitySection } from '../activity/EntityActivitySection';
import { WellPoSpendCharts } from '../wells/WellPoSpendCharts';

interface WellDetailSidePanelProps {
  well: WellMaster;
  activeRegions: RegionMasterDto[];
  onClose: () => void;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  onWellUpdated: () => void | Promise<void>;
  canEditWell: boolean;
}

export function WellDetailSidePanel({
  well,
  activeRegions,
  onClose,
  authFetch,
  onWellUpdated,
  canEditWell
}: WellDetailSidePanelProps): ReactElement {
  const [editing, setEditing] = useState(false);
  const [wellName, setWellName] = useState(well.wellName);
  /** Custom field label when the well is not tied to a region catalog field id. */
  const [customFieldName, setCustomFieldName] = useState(well.fieldId ? '' : well.field);
  const [fieldId, setFieldId] = useState(well.fieldId);
  const [region, setRegion] = useState(well.region);
  const [state, setState] = useState(well.state);
  const [country, setCountry] = useState(well.country);
  const [depthFt, setDepthFt] = useState(
    well.depthFt !== undefined && well.depthFt !== null ? String(well.depthFt) : ''
  );
  const [reservoirType, setReservoirType] = useState(well.reservoirType);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const regionOptions = activeRegions.filter((r) => r.isActive);

  const wellRegionFieldInfo = useMemo(
    () => toWellRegionFieldInfo(findRegionField(activeRegions, well.region, well.fieldId)),
    [activeRegions, well.region, well.fieldId]
  );

  const fieldOptions: RegionFieldDto[] = useMemo(() => {
    const row = activeRegions.find((r) => r.code === region);
    return row?.fields ?? [];
  }, [activeRegions, region]);

  useEffect(() => {
    setWellName(well.wellName);
    setCustomFieldName(well.fieldId ? '' : well.field);
    setFieldId(well.fieldId);
    setRegion(well.region);
    setState(well.state);
    setCountry(well.country);
    setDepthFt(well.depthFt !== undefined && well.depthFt !== null ? String(well.depthFt) : '');
    setReservoirType(well.reservoirType);
    setSaveError('');
    setEditing(false);
  }, [
    well.id,
    well.wellName,
    well.field,
    well.fieldId,
    well.region,
    well.state,
    well.country,
    well.depthFt,
    well.reservoirType
  ]);

  async function saveWell(): Promise<void> {
    setSaveError('');
    if (!region) {
      setSaveError('Select a region.');
      return;
    }
    if (!wellName.trim()) {
      setSaveError('Well name is required.');
      return;
    }
    setSaving(true);
    try {
      const depthNum = depthFt.trim() === '' ? null : Number(depthFt);
      if (depthFt.trim() !== '' && Number.isNaN(depthNum)) {
        setSaveError('Depth must be a number.');
        setSaving(false);
        return;
      }
      const response = await authFetch(`${WELLS_API_URL}/${well.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          wellName: wellName.trim(),
          field: fieldId.trim() ? '' : customFieldName.trim(),
          fieldId: fieldId.trim() || null,
          fieldCounty: fieldId.trim() ? null : '',
          fieldState: fieldId.trim() ? null : '',
          region,
          state: state.trim(),
          country: country.trim(),
          depthFt: depthNum,
          reservoirType: reservoirType.trim() || null
        })
      });
      if (!response.ok) {
        const text = await response.text();
        setSaveError(text || 'Could not save well.');
        return;
      }
      await onWellUpdated();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside className="side-panel" aria-label="Well details">
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          <h3>{well.wellName || well.wellKey}</h3>
        </div>
        <div className="side-panel-actions">
          {canEditWell && !editing && (
            <button type="button" className="secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>
      </div>
      <div className="side-panel-body">
        {editing && canEditWell ? (
          <div
            className="well-edit-form"
            role="form"
            aria-labelledby="well-edit-title"
            aria-describedby="well-edit-desc"
          >
            <header className="well-edit-form__header">
              <div>
                <h3 id="well-edit-title" className="well-edit-form__title">
                  Edit well
                </h3>
                <p id="well-edit-desc" className="well-edit-form__subtitle">
                  Update directory fields for this well. The well key cannot be changed.
                </p>
              </div>
            </header>

            <div className="well-edit-form__body grid">
              <section className="well-edit-form__section" aria-labelledby="well-edit-sec-identity">
                <h4 className="form-section-title" id="well-edit-sec-identity">
                  Identity
                </h4>
                <label className="full-width">
                  Well key
                  <input
                    value={well.wellKey}
                    readOnly
                    className="well-edit-form__readonly"
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </label>
                <label className="full-width">
                  Well name
                  <input
                    value={wellName}
                    onChange={(e) => setWellName(e.target.value)}
                    placeholder="Display name"
                    autoComplete="off"
                  />
                </label>
              </section>

              <section className="well-edit-form__section" aria-labelledby="well-edit-sec-region">
                <h4 className="form-section-title" id="well-edit-sec-region">
                  Region &amp; field
                </h4>
                <label className="full-width">
                  Region
                  <select
                    value={region}
                    onChange={(e) => {
                      setRegion(e.target.value);
                      setFieldId('');
                    }}
                  >
                    <option value="">Select region…</option>
                    {regionOptions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="full-width">
                  Field (region catalog)
                  <select
                    value={fieldId}
                    disabled={!region || fieldOptions.length === 0}
                    onChange={(e) => setFieldId(e.target.value)}
                  >
                    <option value="">
                      {!region
                        ? 'Select a region…'
                        : fieldOptions.length === 0
                          ? 'No fields configured for this region'
                          : 'Select field…'}
                    </option>
                    {fieldOptions.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name} — {f.county}, {f.state}
                      </option>
                    ))}
                  </select>
                </label>
                {!fieldId.trim() ? (
                  <label className="full-width">
                    Field name (custom)
                    <input
                      value={customFieldName}
                      onChange={(e) => setCustomFieldName(e.target.value)}
                      placeholder="If not using catalog"
                      autoComplete="off"
                    />
                  </label>
                ) : null}
              </section>

              <section className="well-edit-form__section" aria-labelledby="well-edit-sec-location">
                <h4 className="form-section-title" id="well-edit-sec-location">
                  Location
                </h4>
                <div className="row full-width well-edit-form__row-two">
                  <label>
                    State
                    <input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. TX" autoComplete="off" />
                  </label>
                  <label>
                    Country
                    <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. USA" autoComplete="off" />
                  </label>
                </div>
              </section>

              <section className="well-edit-form__section" aria-labelledby="well-edit-sec-tech">
                <h4 className="form-section-title" id="well-edit-sec-tech">
                  Technical
                </h4>
                <label className="full-width">
                  Depth (ft)
                  <input
                    type="text"
                    inputMode="decimal"
                    value={depthFt}
                    onChange={(e) => setDepthFt(e.target.value)}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                </label>
                <label className="full-width">
                  Reservoir type
                  <input
                    value={reservoirType}
                    onChange={(e) => setReservoirType(e.target.value)}
                    placeholder="e.g. Sandstone"
                    autoComplete="off"
                  />
                </label>
              </section>
            </div>

            <footer className="well-edit-form__footer">
              {saveError ? (
                <p className="error well-edit-form__error" role="alert">
                  {saveError}
                </p>
              ) : null}
              <div className="well-edit-form__actions">
                <button
                  type="button"
                  className="secondary"
                  disabled={saving}
                  onClick={() => {
                    setWellName(well.wellName);
                    setCustomFieldName(well.fieldId ? '' : well.field);
                    setFieldId(well.fieldId);
                    setRegion(well.region);
                    setState(well.state);
                    setCountry(well.country);
                    setDepthFt(well.depthFt !== undefined && well.depthFt !== null ? String(well.depthFt) : '');
                    setReservoirType(well.reservoirType);
                    setSaveError('');
                    setEditing(false);
                  }}
                >
                  Cancel
                </button>
                <button type="button" disabled={saving} onClick={() => void saveWell()}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </footer>
          </div>
        ) : (
          <Fragment>
          <dl className="detail-list">
            <dt>API</dt>
            <dd className="mono-id">{well.wellKey}</dd>
            <dt>Well name</dt>
            <dd>{well.wellName}</dd>
          </dl>
          <section className="nested-card well-region-field-info-block" aria-label="Region field info">
            <h4 className="form-section-title">Region field info</h4>
            <dl className="detail-list">
              <dt>Field name</dt>
              <dd>{wellRegionFieldInfo?.fieldName || well.field?.trim() || '—'}</dd>
              <dt>Nearest city</dt>
              <dd>{wellRegionFieldInfo?.closestCity?.trim() || '—'}</dd>
              <dt>Miles from city</dt>
              <dd>
                {wellRegionFieldInfo?.milesFromCity !== undefined &&
                wellRegionFieldInfo.milesFromCity !== null &&
                !Number.isNaN(wellRegionFieldInfo.milesFromCity)
                  ? wellRegionFieldInfo.milesFromCity
                  : '—'}
              </dd>
            </dl>
          </section>
          <dl className="detail-list">
            <dt>Region</dt>
            <dd>{well.region ? regionDisplayName(well.region, activeRegions) : '—'}</dd>
            <dt>State</dt>
            <dd>{well.state}</dd>
            <dt>Country</dt>
            <dd>{well.country}</dd>
            <dt>Depth (ft)</dt>
            <dd>{well.depthFt !== undefined && well.depthFt !== null ? String(well.depthFt) : '—'}</dd>
            <dt>Reservoir</dt>
            <dd>{well.reservoirType}</dd>
            <dt>Record ID</dt>
            <dd className="mono-id">{well.id}</dd>
          </dl>
          </Fragment>
        )}
        <WellPoSpendCharts wellId={well.id} authFetch={authFetch} />
        <EntityActivitySection
          authFetch={authFetch}
          path={`/wells/${well.id}/activity`}
          testId="well-activity"
        />
      </div>
    </aside>
  );
}
